import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import RichTextEditor from "@/components/admin/RichTextEditor";
import ImageUpload from "@/components/admin/ImageUpload";
import MoneyInput from "@/components/admin/MoneyInput";
import { toast } from "sonner";
import { Plus, Trash, ArrowLeft, X } from "@phosphor-icons/react";

const INDUSTRIES_FALLBACK = ["AI", "Healthcare", "Biotech", "FinTech", "SaaS", "ClimateTech", "Manufacturing", "Robotics", "Energy", "MedTech", "Consumer", "Education", "FoodTech"];
const STAGES_FALLBACK = ["Pre-Seed", "Seed", "Series A", "Series B", "Series C", "MBO/MBI"];
const REVENUE_FALLBACK = ["Pre-revenue", "Pilots stage", "Revenue generating"];
const COUNTRIES = ["UK", "Europe", "USA", "India", "Others"];
const PRIORITIES = ["High", "Medium", "Low"];
const UK_REGIONS = [
  "London", "South East", "East of England", "South West", "North West", "North East",
  "Yorkshire and Humber", "Scotland", "West Midlands", "East Midlands", "Northern Ireland", "Wales",
];
const REGIONS = [...UK_REGIONS, "Outside UK"];

const emptyFounder = { name: "", designation: "", photo_url: "", experience: "", linkedin: "", biography: "" };
const emptyTeam = { name: "", designation: "", photo_url: "", category: "leadership" };
const emptyCap = { name: "", percentage: 10, type: "investor" };

const emptyCompany = {
  name: "", tagline: "", description: "", logo_url: "", banner_url: "",
  industry: "SaaS", website: "", linkedin: "", location: "", country: "UK",
  region: "London",
  founded: null, stage: "Series A", revenue_status: "Pre-revenue",
  priority: "Medium", eis: false, seis: false, featured: false, published: true,
  funding_deadline: "", investment_highlights: [],
  problem: "", solution: "", market_opportunity: "", business_model: "",
  competitive_advantage: "", target_customers: "", traction: "", roadmap: "", investment_ask: "",
  fundraising: {
    amount_raising: 1000000, minimum_investment: 10000, equity_offered: 10,
    valuation: 10000000, use_of_funds: "", expected_closing_date: "",
    investor_rights: "", exit_strategy: "", amount_raised: 0, investor_count: 0,
    total_funding_raised: 0, grants_amount: 0, equity_loans_amount: 0,
  },
  hidden_overview_sections: [], hidden_tabs: [],
  metrics: {},
  founders: [], team: [], cap_table: [],
  client_email: "", client_emails: [], client_name: "",
};

const Field = ({ label, children, cols = "col-span-2" }) => (
  <div className={cols}>
    <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block font-semibold">{label}</Label>
    {children}
  </div>
);

const RichField = ({ label, value, onChange, placeholder, testId, hidden = false }) => (
  <div className={`col-span-2 relative ${hidden ? "opacity-50 pointer-events-none" : ""}`}>
    <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block font-semibold flex items-center gap-2">
      {label}
      {hidden && (
        <span className="normal-case tracking-normal text-[10px] font-bold text-amber-800 dark:text-amber-300 bg-amber-100 dark:bg-amber-950/60 px-2 py-0.5 rounded-md">
          Hidden on public page
        </span>
      )}
    </Label>
    <RichTextEditor value={value} onChange={onChange} placeholder={placeholder} testId={testId} />
  </div>
);

const Section = ({ title, children, hidden = false }) => (
  <section className={`rounded-2xl border border-border bg-card p-6 md:p-8 transition-opacity ${hidden ? "opacity-60" : ""}`}>
    <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
      <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-emerald-700 dark:text-emerald-400">{title}</div>
      {hidden && (
        <span className="text-[10px] uppercase tracking-wider bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 px-2.5 py-1 rounded-md font-bold">
          Hidden on public page
        </span>
      )}
    </div>
    <div className={hidden ? "pointer-events-none" : ""}>{children}</div>
  </section>
);

// Multi-email input for company Client Access. Enter/comma/blur commits the
// current draft as a chip; ⌫ on an empty draft removes the last chip.
const MultiEmailField = ({ label, emails, onChange }) => {
  const [draft, setDraft] = React.useState("");
  const [error, setError] = React.useState("");
  const commit = () => {
    const raw = draft.trim().replace(/,+$/, "");
    if (!raw) return;
    // very light validation — server does the authoritative check
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) {
      setError("Enter a valid email address");
      return;
    }
    const next = Array.from(new Set([...(emails || []), raw.toLowerCase()]));
    onChange(next);
    setDraft("");
    setError("");
  };
  return (
    <div className="col-span-1">
      <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block font-semibold">{label}</Label>
      <div className="flex flex-wrap gap-1.5 min-h-[42px] p-1.5 rounded-md border border-input bg-background" data-testid="client-emails-input-wrap">
        {(emails || []).map((e) => (
          <span key={e} className="inline-flex items-center gap-1.5 pl-2 pr-1 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 text-xs font-mono" data-testid={`client-email-chip-${e}`}>
            {e}
            <button
              type="button"
              onClick={() => onChange((emails || []).filter((x) => x !== e))}
              className="rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/50 h-4 w-4 grid place-items-center"
              title="Remove"
              data-testid={`remove-client-email-${e}`}
            >
              <X size={10} />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setError(""); }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commit(); }
            else if (e.key === "Backspace" && !draft && (emails || []).length) {
              onChange((emails || []).slice(0, -1));
            }
          }}
          onBlur={commit}
          placeholder={(emails || []).length ? "" : "ceo@company.com  (Enter to add another)"}
          className="flex-1 min-w-[180px] bg-transparent outline-none text-sm px-1"
          data-testid="client-email-add-input"
        />
      </div>
      {error && <div className="mt-1 text-xs text-red-600" data-testid="client-emails-error">{error}</div>}
    </div>
  );
};

export default function AdminCompanyForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const { isClient } = useAuth();
  const [form, setForm] = useState(emptyCompany);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState("");
  // Dynamic filter option lists — sourced from /admin/filters so any additions
  // by admins flow through here automatically.
  const [industries, setIndustries] = useState(INDUSTRIES_FALLBACK);
  const [stages, setStages] = useState(STAGES_FALLBACK);
  const [revenueStatuses, setRevenueStatuses] = useState(REVENUE_FALLBACK);

  useEffect(() => {
    api.get("/companies/filters/options").then((r) => {
      const d = r.data || {};
      if (Array.isArray(d.industries) && d.industries.length) setIndustries(d.industries);
      if (Array.isArray(d.stages) && d.stages.length) setStages(d.stages);
      if (Array.isArray(d.revenue_statuses) && d.revenue_statuses.length) setRevenueStatuses(d.revenue_statuses);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get(`/companies/${id}`).then((r) => {
      const d = r.data;
      setForm({
        ...emptyCompany,
        ...d,
        fundraising: { ...emptyCompany.fundraising, ...(d.fundraising || {}) },
        metrics: d.metrics || {},
        founders: d.founders || [],
        team: d.team || [],
        cap_table: d.cap_table || [],
        investment_highlights: d.investment_highlights || [],
        funding_deadline: d.funding_deadline || "",
      });
    });
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setFR = (k, v) => setForm((f) => ({ ...f, fundraising: { ...f.fundraising, [k]: v } }));
  const setM = (k, v) => setForm((f) => ({ ...f, metrics: { ...f.metrics, [k]: v } }));

  // Founders
  const addFounder = () => setForm((f) => ({ ...f, founders: [...f.founders, { ...emptyFounder }] }));
  const removeFounder = (i) => setForm((f) => ({ ...f, founders: f.founders.filter((_, idx) => idx !== i) }));
  const updateFounder = (i, k, v) => setForm((f) => ({ ...f, founders: f.founders.map((x, idx) => idx === i ? { ...x, [k]: v } : x) }));

  // Team
  const addTeam = () => setForm((f) => ({ ...f, team: [...f.team, { ...emptyTeam }] }));
  const removeTeam = (i) => setForm((f) => ({ ...f, team: f.team.filter((_, idx) => idx !== i) }));
  const updateTeam = (i, k, v) => setForm((f) => ({ ...f, team: f.team.map((x, idx) => idx === i ? { ...x, [k]: v } : x) }));

  // Cap table
  const addCap = () => setForm((f) => ({ ...f, cap_table: [...f.cap_table, { ...emptyCap }] }));
  const removeCap = (i) => setForm((f) => ({ ...f, cap_table: f.cap_table.filter((_, idx) => idx !== i) }));
  const updateCap = (i, k, v) => setForm((f) => ({ ...f, cap_table: f.cap_table.map((x, idx) => idx === i ? { ...x, [k]: v } : x) }));

  // Historical financials (year-by-year revenue + ebitda)
  const historicalRows = form.metrics.historical_revenue || [];
  const addHistRow = () => {
    const lastYear = historicalRows.length ? Math.max(...historicalRows.map((r) => Number(r.year) || 0)) : new Date().getFullYear() - 1;
    setM("historical_revenue", [...historicalRows, { year: lastYear + 1, revenue: 0, ebitda: 0 }]);
  };
  const removeHistRow = (i) => setM("historical_revenue", historicalRows.filter((_, idx) => idx !== i));
  const updateHistRow = (i, k, v) => setM("historical_revenue", historicalRows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));

  const submit = async () => {
    setLoading(true);
    try {
      const payload = JSON.parse(JSON.stringify(form));
      const fr = payload.fundraising;
      ["amount_raising", "minimum_investment", "equity_offered", "valuation", "amount_raised", "investor_count", "total_funding_raised", "grants_amount", "equity_loans_amount"].forEach((k) => {
        fr[k] = fr[k] === "" || fr[k] === null ? 0 : Number(fr[k]);
      });
      const m = payload.metrics;
      Object.keys(m).forEach((k) => {
        if (k === "ip_status" || k === "historical_revenue") return;
        if (m[k] === "" || m[k] === null) m[k] = null;
        else m[k] = Number(m[k]);
      });
      payload.cap_table = payload.cap_table.map((e) => ({ ...e, percentage: Number(e.percentage) || 0 }));
      // Normalize historical revenue rows
      if (Array.isArray(m.historical_revenue)) {
        m.historical_revenue = m.historical_revenue
          .filter((r) => r && r.year !== "" && r.year !== null)
          .map((r) => ({
            year: Number(r.year) || 0,
            revenue: Number(r.revenue) || 0,
            ebitda: Number(r.ebitda) || 0,
          }))
          .sort((a, b) => a.year - b.year);
      }
      if (payload.founded === "" || payload.founded === null) payload.founded = null;
      else payload.founded = Number(payload.founded);

      if (id) await api.put(`/companies/${id}`, payload);
      else await api.post("/companies", payload);
      // Sync compliance fields via the dedicated endpoint (admin/team only, and only when editing an existing record)
      if (id && !isClient) {
        try {
          await api.patch(`/admin/companies/${id}/compliance`, {
            compliance_status: form.compliance_status || null,
            compliance_notes: form.compliance_notes ?? null,
            next_compliance_review: form.next_compliance_review || null,
          });
        } catch { /* keep the main save success even if compliance patch fails */ }
      }
      toast.success(id ? (isClient ? "Changes submitted for review" : "Company updated") : "Company created");
      nav(isClient ? "/" : "/admin/companies");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-5xl">
      <button onClick={() => nav(-1)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4"><ArrowLeft size={14} /> Back</button>
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Admin</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">{id ? "Edit company" : "New company"}</h1>
        {isClient && id && (
          <div className="mt-4 rounded-xl border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 flex items-start gap-3" data-testid="client-review-notice">
            <div className="h-6 w-6 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 grid place-items-center shrink-0 mt-0.5 text-[11px] font-bold">i</div>
            <div className="text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
              <div className="font-semibold mb-0.5">Changes require Inventya review</div>
              Any edits you save here are held for review by the Inventya investment team before being published to investors.
              The current live version of your profile stays visible to investors until your changes are approved.
              {form._pending_review && <div className="mt-1 font-semibold">You have an unreviewed submission pending. Saving now will replace it.</div>}
            </div>
          </div>
        )}
      </div>

      <div className="space-y-6">
        {/* IDENTITY & BRAND */}
        <Section title="Brand & Identity">
          <div className="grid grid-cols-1 md:grid-cols-[160px_240px_1fr] gap-6">
            <div>
              <ImageUpload label="Logo" value={form.logo_url} onChange={(url) => set("logo_url", url)} aspect="aspect-square" testId="upload-logo" />
            </div>
            <div>
              <ImageUpload label="Banner" value={form.banner_url} onChange={(url) => set("banner_url", url)} aspect="aspect-[4/3]" cropAspect={3} testId="upload-banner" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Company name" cols="col-span-2"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="form-name-input" /></Field>
              <Field label="Tagline" cols="col-span-2"><Input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} data-testid="form-tagline-input" /></Field>
              <Field label="Website" cols=""><Input value={form.website || ""} onChange={(e) => set("website", e.target.value)} placeholder="https://…" /></Field>
              <Field label="LinkedIn" cols=""><Input value={form.linkedin || ""} onChange={(e) => set("linkedin", e.target.value)} /></Field>
            </div>
          </div>
        </Section>

        {/* CLASSIFICATION */}
        <Section title="Classification">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Field label="Industry" cols="">
              <Select value={form.industry} onValueChange={(v) => set("industry", v)}>
                <SelectTrigger data-testid="form-industry-select"><SelectValue /></SelectTrigger>
                <SelectContent>{industries.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Country" cols="">
              <Select value={form.country} onValueChange={(v) => set("country", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Region" cols="">
              <Select value={form.region || "London"} onValueChange={(v) => set("region", v)}>
                <SelectTrigger data-testid="form-region-select"><SelectValue /></SelectTrigger>
                <SelectContent>{REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Location (city)" cols=""><Input value={form.location} onChange={(e) => set("location", e.target.value)} /></Field>
            <Field label="Founded (year)" cols=""><Input type="number" value={form.founded || ""} onChange={(e) => set("founded", e.target.value)} /></Field>
            <Field label="Stage" cols="">
              <Select value={form.stage} onValueChange={(v) => set("stage", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Revenue status" cols="">
              <Select value={form.revenue_status} onValueChange={(v) => set("revenue_status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{revenueStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Priority" cols="">
              <Select value={form.priority} onValueChange={(v) => set("priority", v)} disabled={isClient}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Funding deadline" cols=""><Input type="date" value={form.funding_deadline || ""} onChange={(e) => set("funding_deadline", e.target.value)} /></Field>
            <div className="flex gap-6 col-span-2 md:col-span-3 pt-2 flex-wrap">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch checked={!!form.eis} onCheckedChange={(v) => set("eis", v)} data-testid="form-eis-switch" /> EIS Eligible
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch checked={!!form.seis} onCheckedChange={(v) => set("seis", v)} /> SEIS Eligible
              </label>
              {!isClient && (
                <>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Switch checked={!!form.featured} onCheckedChange={(v) => set("featured", v)} /> Featured
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Switch checked={!!form.published} onCheckedChange={(v) => set("published", v)} /> Published
                  </label>
                </>
              )}
            </div>
          </div>
        </Section>

        {/* CLIENT ACCESS — admins only */}
        {!isClient && (
          <Section title="Client Access">
            <p className="text-sm text-muted-foreground mb-4">
              Assign one or more investee-company representatives (e.g. multiple directors).
              They&apos;ll each be able to sign in with their email and edit this raise + upload documents —
              but they won&apos;t see any other companies. Leave blank if no client access is needed.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <MultiEmailField
                label="Client emails"
                emails={
                  // Merge the legacy single-email field with the new list, deduped.
                  Array.from(new Set([
                    ...(form.client_email ? [form.client_email] : []),
                    ...((form.client_emails || []).filter(Boolean)),
                  ]))
                }
                onChange={(list) => {
                  const cleaned = Array.from(new Set(list.map((e) => (e || "").trim().toLowerCase()).filter(Boolean)));
                  // Persist to the multi-email array. Also mirror the first one into `client_email`
                  // so the back-compat single-owner path (used elsewhere in the API) still works.
                  set("client_emails", cleaned);
                  set("client_email", cleaned[0] || "");
                }}
              />
              <Field label="Client name (optional, first director)" cols="">
                <Input
                  value={form.client_name || ""}
                  onChange={(e) => set("client_name", e.target.value)}
                  placeholder="Full name"
                  data-testid="form-client-name-input"
                />
              </Field>
            </div>
          </Section>
        )}

        {/* FCA Compliance Workflow — visible to admins/team, hidden from clients */}
        {!isClient && id && (
          <Section title="FCA Compliance Workflow">
            <p className="text-sm text-muted-foreground mb-4">
              Each Investment Opportunity is a separate Financial Promotion under FCA rules. This section tracks
              the compliance review lifecycle. Investors only see companies in <b>Published</b> state.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Compliance status">
                <Select value={form.compliance_status || "Draft"} onValueChange={(v) => set("compliance_status", v)}>
                  <SelectTrigger data-testid="form-compliance-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Draft","Internal Review","Compliance Review","Approved by FCA Partner","Published","Archived"].map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Next compliance review">
                <Input type="date" value={form.next_compliance_review || ""} onChange={(e) => set("next_compliance_review", e.target.value)} data-testid="form-next-review" />
              </Field>
              <Field label="Compliance version">
                <Input value={form.compliance_version ? `v${form.compliance_version}` : "—"} disabled className="num-display" />
              </Field>
              <div className="md:col-span-3">
                <Field label="Compliance notes (internal)">
                  <textarea
                    className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.compliance_notes || ""}
                    onChange={(e) => set("compliance_notes", e.target.value)}
                    placeholder="Reviewer notes, outstanding items, sign-off references…"
                    data-testid="form-compliance-notes"
                  />
                </Field>
              </div>
              {form.compliance_approved_by_name && (
                <div className="md:col-span-3 text-xs text-muted-foreground border-t border-border pt-3">
                  Last approved by <b>{form.compliance_approved_by_name}</b> on {form.compliance_approval_date && new Date(form.compliance_approval_date).toLocaleDateString()}
                  {form.last_compliance_review && <> · Last review {new Date(form.last_compliance_review).toLocaleDateString()}</>}
                </div>
              )}
            </div>
          </Section>
        )}

        {/* SECTION VISIBILITY — Overview subsections + Tabs. Placed BEFORE
             Overview so admins pick what to show first; the corresponding
             fields below then dim automatically. */}
        <Section title="Page Visibility">
          <p className="text-sm text-muted-foreground mb-5">
            Toggle what investors see on this company&apos;s page. Any turned-off section is dimmed below and won&apos;t save changes — Overview and Data Room tabs are always visible. The layout on the public page re-flows to fill any freed-up space.
          </p>

          <div className="mb-8">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-3 block font-semibold">Overview sections</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                ["problem", "Problem"],
                ["solution", "Solution"],
                ["market_opportunity", "Market Opportunity"],
                ["business_model", "Business Model"],
                ["competitive_advantage", "Competitive Advantage"],
                ["target_customers", "Target Customers"],
                ["traction", "Traction"],
                ["investment_ask", "Investment Ask"],
                ["roadmap", "Roadmap"],
              ].map(([key, label]) => {
                const hidden = (form.hidden_overview_sections || []).includes(key);
                return (
                  <label
                    key={key}
                    className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-border bg-background hover:bg-secondary/40 cursor-pointer"
                    data-testid={`toggle-section-${key}`}
                  >
                    <span className="text-sm font-medium">{label}</span>
                    <Switch
                      checked={!hidden}
                      onCheckedChange={(checked) => {
                        const cur = new Set(form.hidden_overview_sections || []);
                        if (checked) cur.delete(key);
                        else cur.add(key);
                        set("hidden_overview_sections", Array.from(cur));
                      }}
                    />
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-3 block font-semibold">
              Tabs <span className="normal-case text-muted-foreground/70">(Overview and Data Room are always visible)</span>
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                ["metrics", "Key Metrics"],
                ["founders", "Founders"],
                ["team", "Team"],
                ["financials", "Financials"],
                ["fundraising", "Fundraising"],
                ["captable", "Cap Table"],
              ].map(([key, label]) => {
                const hidden = (form.hidden_tabs || []).includes(key);
                return (
                  <label
                    key={key}
                    className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-border bg-background hover:bg-secondary/40 cursor-pointer"
                    data-testid={`toggle-tab-${key}`}
                  >
                    <span className="text-sm font-medium">{label}</span>
                    <Switch
                      checked={!hidden}
                      onCheckedChange={(checked) => {
                        const cur = new Set(form.hidden_tabs || []);
                        if (checked) cur.delete(key);
                        else cur.add(key);
                        set("hidden_tabs", Array.from(cur));
                      }}
                    />
                  </label>
                );
              })}
            </div>
          </div>
        </Section>

        {/* OVERVIEW – rich text (individual fields dim when their section is hidden) */}
        <Section title="Overview">
          <div className="grid grid-cols-1 gap-5">
            <RichField label="Description" value={form.description} onChange={(v) => set("description", v)} placeholder="Company overview…" testId="rte-description" />
            <RichField label="Problem" value={form.problem} onChange={(v) => set("problem", v)} testId="rte-problem" hidden={(form.hidden_overview_sections || []).includes("problem")} />
            <RichField label="Solution" value={form.solution} onChange={(v) => set("solution", v)} hidden={(form.hidden_overview_sections || []).includes("solution")} />
            <RichField label="Market opportunity" value={form.market_opportunity} onChange={(v) => set("market_opportunity", v)} hidden={(form.hidden_overview_sections || []).includes("market_opportunity")} />
            <RichField label="Business model" value={form.business_model} onChange={(v) => set("business_model", v)} hidden={(form.hidden_overview_sections || []).includes("business_model")} />
            <RichField label="Competitive advantage" value={form.competitive_advantage} onChange={(v) => set("competitive_advantage", v)} hidden={(form.hidden_overview_sections || []).includes("competitive_advantage")} />
            <RichField label="Target customers" value={form.target_customers} onChange={(v) => set("target_customers", v)} hidden={(form.hidden_overview_sections || []).includes("target_customers")} />
            <RichField label="Traction" value={form.traction} onChange={(v) => set("traction", v)} hidden={(form.hidden_overview_sections || []).includes("traction")} />
            <RichField label="Roadmap" value={form.roadmap} onChange={(v) => set("roadmap", v)} hidden={(form.hidden_overview_sections || []).includes("roadmap")} />
            <RichField label="Investment ask" value={form.investment_ask} onChange={(v) => set("investment_ask", v)} hidden={(form.hidden_overview_sections || []).includes("investment_ask")} />
            <Field label="Investment highlights">
              <div className="flex gap-2 mb-2">
                <Input value={highlight} onChange={(e) => setHighlight(e.target.value)} placeholder="Add a highlight and press Add…" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (highlight) { set("investment_highlights", [...form.investment_highlights, highlight]); setHighlight(""); } } }} />
                <Button type="button" onClick={() => { if (highlight) { set("investment_highlights", [...form.investment_highlights, highlight]); setHighlight(""); } }}>Add</Button>
              </div>
              <div className="space-y-1">
                {form.investment_highlights.map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm p-2 rounded bg-secondary">
                    <span className="flex-1">{h}</span>
                    <button onClick={() => set("investment_highlights", form.investment_highlights.filter((_, idx) => idx !== i))} className="text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 p-1 rounded"><Trash size={14} /></button>
                  </div>
                ))}
              </div>
            </Field>
          </div>
        </Section>

        {/* FUNDRAISING */}
        <Section title="Fundraising" hidden={(form.hidden_tabs || []).includes("fundraising")}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Field label="Amount raising" cols=""><MoneyInput value={form.fundraising.amount_raising} onChange={(v) => setFR("amount_raising", v)} testId="fr-amount-raising" /></Field>
            <Field label="Minimum investment" cols=""><MoneyInput value={form.fundraising.minimum_investment} onChange={(v) => setFR("minimum_investment", v)} testId="fr-min-investment" /></Field>
            <Field label="Equity offered (%)" cols=""><Input type="number" step="0.1" value={form.fundraising.equity_offered} onChange={(e) => setFR("equity_offered", e.target.value)} /></Field>
            <Field label="Pre-money valuation" cols=""><MoneyInput value={form.fundraising.valuation || ""} onChange={(v) => setFR("valuation", v)} testId="fr-valuation" /></Field>
            <Field label="Expected closing date" cols=""><Input type="date" value={form.fundraising.expected_closing_date || ""} onChange={(e) => setFR("expected_closing_date", e.target.value)} /></Field>
            <div />
            <Field label="Total funding raised so far" cols=""><MoneyInput value={form.fundraising.total_funding_raised || ""} onChange={(v) => setFR("total_funding_raised", v)} testId="fr-total-raised" /></Field>
            <Field label="Grants amount" cols=""><MoneyInput value={form.fundraising.grants_amount || ""} onChange={(v) => setFR("grants_amount", v)} testId="fr-grants" /></Field>
            <Field label="Equity / Loans amount" cols=""><MoneyInput value={form.fundraising.equity_loans_amount || ""} onChange={(v) => setFR("equity_loans_amount", v)} testId="fr-equity-loans" /></Field>
            <RichField label="Use of funds" value={form.fundraising.use_of_funds} onChange={(v) => setFR("use_of_funds", v)} />
            <RichField label="Investor rights" value={form.fundraising.investor_rights} onChange={(v) => setFR("investor_rights", v)} />
            <RichField label="Exit strategy" value={form.fundraising.exit_strategy} onChange={(v) => setFR("exit_strategy", v)} />
          </div>
        </Section>

        {/* KEY METRICS */}
        <Section title="Key Metrics" hidden={(form.hidden_tabs || []).includes("metrics")}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              ["annual_revenue", "Annual revenue"], ["mrr", "MRR"], ["arr", "ARR"],
              ["growth_rate", "Growth rate %"], ["gross_margin", "Gross margin %"], ["ebitda", "EBITDA"],
              ["customers", "Customers"], ["employees", "Employees"], ["countries_served", "Countries served"],
              ["patents", "Patents"], ["runway_months", "Runway (months)"], ["burn_rate", "Burn rate (monthly)"],
              ["valuation", "Valuation"], ["previous_funding", "Previous funding"],
            ].map(([k, label]) => (
              <Field key={k} label={label} cols=""><Input type="number" value={form.metrics[k] ?? ""} onChange={(e) => setM(k, e.target.value)} /></Field>
            ))}
            <Field label="IP status" cols=""><Input value={form.metrics.ip_status || ""} onChange={(e) => setM("ip_status", e.target.value)} /></Field>
          </div>
        </Section>

        {/* HISTORICAL FINANCIALS – year-by-year for the Financials chart */}
        <Section title="Historical Financials" hidden={(form.hidden_tabs || []).includes("financials")}>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">Add revenue and EBITDA figures per year. Powers the Revenue &amp; EBITDA charts on the company page.</p>
            <Button size="sm" variant="outline" onClick={addHistRow} data-testid="add-hist-btn"><Plus size={14} className="mr-1" /> Add year</Button>
          </div>
          {historicalRows.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">No historical financials yet.</div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-[120px_1fr_1fr_40px] gap-3 text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-bold px-1">
                <div>Year</div>
                <div>Revenue</div>
                <div>EBITDA</div>
                <div />
              </div>
              {historicalRows.map((row, i) => (
                <div key={i} className="grid grid-cols-[120px_1fr_1fr_40px] gap-3 items-center">
                  <Input
                    type="number"
                    placeholder="Year"
                    value={row.year ?? ""}
                    onChange={(e) => updateHistRow(i, "year", e.target.value)}
                    data-testid={`hist-year-${i}`}
                  />
                  <MoneyInput
                    value={row.revenue ?? ""}
                    onChange={(v) => updateHistRow(i, "revenue", v)}
                    placeholder="Revenue (e.g., 1200000)"
                    testId={`hist-revenue-${i}`}
                  />
                  <MoneyInput
                    value={row.ebitda ?? ""}
                    onChange={(v) => updateHistRow(i, "ebitda", v)}
                    placeholder="EBITDA (may be negative)"
                    testId={`hist-ebitda-${i}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeHistRow(i)}
                    className="h-9 w-9 rounded grid place-items-center text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                    title="Remove year"
                    data-testid={`hist-remove-${i}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* FOUNDERS */}
        <Section title="Founders" hidden={(form.hidden_tabs || []).includes("founders")}>
          <div className="flex justify-end mb-4">
            <Button size="sm" variant="outline" onClick={addFounder} data-testid="add-founder-btn"><Plus size={14} className="mr-1" /> Add founder</Button>
          </div>
          <div className="space-y-4">
            {form.founders.map((f, i) => (
              <div key={i} className="border border-border rounded-xl p-5 relative bg-secondary/20">
                <button onClick={() => removeFounder(i)} className="absolute top-3 right-3 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 p-1.5 rounded" title="Remove"><Trash size={14} /></button>
                <div className="grid grid-cols-1 md:grid-cols-[120px_1fr] gap-4">
                  <ImageUpload label="Photo" value={f.photo_url} onChange={(url) => updateFounder(i, "photo_url", url)} aspect="aspect-square" testId={`upload-founder-${i}`} />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Name" cols=""><Input value={f.name} onChange={(e) => updateFounder(i, "name", e.target.value)} /></Field>
                    <Field label="Designation" cols=""><Input value={f.designation} onChange={(e) => updateFounder(i, "designation", e.target.value)} /></Field>
                    <Field label="LinkedIn" cols=""><Input value={f.linkedin || ""} onChange={(e) => updateFounder(i, "linkedin", e.target.value)} /></Field>
                    <Field label="Experience" cols=""><Input value={f.experience || ""} onChange={(e) => updateFounder(i, "experience", e.target.value)} placeholder="Ex-Google, PhD Oxford…" /></Field>
                    <RichField label="Biography" value={f.biography} onChange={(v) => updateFounder(i, "biography", v)} />
                  </div>
                </div>
              </div>
            ))}
            {form.founders.length === 0 && <div className="text-sm text-muted-foreground italic">No founders added.</div>}
          </div>
        </Section>

        {/* TEAM */}
        <Section title="Team" hidden={(form.hidden_tabs || []).includes("team")}>
          <div className="flex justify-end mb-4">
            <Button size="sm" variant="outline" onClick={addTeam} data-testid="add-team-btn"><Plus size={14} className="mr-1" /> Add team member</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {form.team.map((t, i) => (
              <div key={i} className="border border-border rounded-xl p-5 relative bg-secondary/20">
                <button onClick={() => removeTeam(i)} className="absolute top-3 right-3 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 p-1.5 rounded"><Trash size={14} /></button>
                <div className="grid grid-cols-[80px_1fr] gap-3">
                  <ImageUpload value={t.photo_url} onChange={(url) => updateTeam(i, "photo_url", url)} aspect="aspect-square" testId={`upload-team-${i}`} />
                  <div className="space-y-2">
                    <Input placeholder="Name" value={t.name} onChange={(e) => updateTeam(i, "name", e.target.value)} />
                    <Input placeholder="Designation" value={t.designation} onChange={(e) => updateTeam(i, "designation", e.target.value)} />
                    <Select value={t.category} onValueChange={(v) => updateTeam(i, "category", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="leadership">Leadership</SelectItem>
                        <SelectItem value="employee">Key Employee</SelectItem>
                        <SelectItem value="advisor">Advisor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
            {form.team.length === 0 && <div className="text-sm text-muted-foreground italic col-span-2">No team members added.</div>}
          </div>
        </Section>

        {/* CAP TABLE */}
        <Section title="Cap Table" hidden={(form.hidden_tabs || []).includes("captable")}>
          <div className="flex justify-end mb-4">
            <Button size="sm" variant="outline" onClick={addCap} data-testid="add-cap-btn"><Plus size={14} className="mr-1" /> Add entry</Button>
          </div>
          <div className="space-y-2">
            {form.cap_table.map((c, i) => (
              <div key={i} className="grid grid-cols-[1fr_120px_180px_40px] gap-2 items-center">
                <Input placeholder="Shareholder name" value={c.name} onChange={(e) => updateCap(i, "name", e.target.value)} />
                <Input type="number" step="0.1" placeholder="%" value={c.percentage} onChange={(e) => updateCap(i, "percentage", e.target.value)} />
                <Select value={c.type} onValueChange={(v) => updateCap(i, "type", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="founder">Founder</SelectItem>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="option_pool">Option Pool</SelectItem>
                    <SelectItem value="investor">Investor</SelectItem>
                    <SelectItem value="shareholder">Shareholder</SelectItem>
                  </SelectContent>
                </Select>
                <button onClick={() => removeCap(i)} className="h-9 w-9 rounded grid place-items-center text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"><Trash size={14} /></button>
              </div>
            ))}
            {form.cap_table.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Total</span>
                <span className={`num-display font-bold ${Math.abs(form.cap_table.reduce((s, e) => s + (Number(e.percentage) || 0), 0) - 100) < 0.5 ? "text-emerald-600" : "text-amber-600"}`}>
                  {form.cap_table.reduce((s, e) => s + (Number(e.percentage) || 0), 0).toFixed(1)}%
                </span>
              </div>
            )}
            {form.cap_table.length === 0 && <div className="text-sm text-muted-foreground italic">No cap table entries.</div>}
          </div>
        </Section>

        {/* SAVE */}
        <div className="flex justify-end gap-2 sticky bottom-4 bg-background/85 backdrop-blur-sm p-3 rounded-xl border border-border">
          <Button variant="outline" onClick={() => nav(isClient ? "/" : "/admin/companies")}>Cancel</Button>
          <Button onClick={submit} disabled={loading} data-testid="save-company-btn" className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900">
            {loading ? "Saving…" : id ? "Save changes" : "Create company"}
          </Button>
        </div>
      </div>
    </div>
  );
}
